import {
  Box,
  Paper,
  Typography,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";

interface ChartData {
  type: "bar" | "line" | "pie";
  title?: string;
  data: unknown[];
  xKey: string;
  yKeys?: { key: string; name: string; color?: string }[];
  yKey?: string;
  pieDataKey?: string;
  pieNameKey?: string;
  colors?: string[];
}

interface TableData {
  headers: string[];
  rows: (string | number)[][];
}

interface RichContent {
  type: "text" | "chart" | "table" | "list";
  content?: string;
  chart?: ChartData;
  table?: TableData;
  list?: string[];
}

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string | RichContent[];
  timestamp: Date;
}

const COLORS = [
  "#1976d2",
  "#2e7d32",
  "#ed6c02",
  "#d32f2f",
  "#9c27b0",
  "#0288d1",
];

const renderChart = (chart: ChartData, theme: ReturnType<typeof useTheme>) => {
  const {
    type,
    title,
    data,
    xKey,
    yKeys,
    yKey,
    pieDataKey,
    pieNameKey,
    colors = COLORS,
  } = chart;

  if (type === "pie" && pieDataKey && pieNameKey) {
    const pieData = (data as Record<string, unknown>[]).map((item, index) => ({
      id: index,
      value: item[pieDataKey] as number,
      label: item[pieNameKey] as string,
    }));

    return (
      <Box sx={{ width: "100%", minHeight: 300 }} my={2}>
        {title && (
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {title}
          </Typography>
        )}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <PieChart
            series={[
              {
                data: pieData,
                innerRadius: 30,
                outerRadius: 100,
                paddingAngle: 2,
                arcLabel: (item) => `${item.value}`,
              },
            ]}
            width={400}
            height={300}
            slotProps={{
              legend: {
                position: { vertical: "bottom", horizontal: "center" },
              },
            }}
          />
        </Box>
      </Box>
    );
  }

  if (type === "line" && yKeys) {
    const xAxisData = (data as Record<string, unknown>[]).map(
      (item) => item[xKey] as string
    );

    const series = yKeys.map(({ key, name, color }, index) => ({
      data: (data as Record<string, unknown>[]).map(
        (item) => item[key] as number
      ),
      label: name,
      color: color || colors[index % colors.length],
      curve: "monotoneX" as const,
    }));

    return (
      <Box sx={{ width: "100%", minHeight: 300 }} my={2}>
        {title && (
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {title}
          </Typography>
        )}
        <Box sx={{ width: "100%", height: 300 }}>
          <LineChart
            xAxis={[
              {
                scaleType: "point",
                data: xAxisData,
                tickLabelStyle: { fontSize: 12 },
              },
            ]}
            yAxis={[
              {
                tickLabelStyle: { fontSize: 12 },
              },
            ]}
            series={series}
            height={300}
            slotProps={{
              legend: {
                position: { vertical: "top", horizontal: "end" },
              },
            }}
            grid={{ vertical: true, horizontal: true }}
          />
        </Box>
      </Box>
    );
  }

  const xAxisData = (data as Record<string, unknown>[]).map(
    (item) => item[xKey] as string
  );

  const series = yKeys
    ? yKeys.map(({ key, name, color }, index) => ({
        data: (data as Record<string, unknown>[]).map(
          (item) => item[key] as number
        ),
        label: name,
        color: color || colors[index % colors.length],
      }))
    : yKey
    ? [
        {
          data: (data as Record<string, unknown>[]).map(
            (item) => item[yKey] as number
          ),
          color: colors[0],
        },
      ]
    : [];

  return (
    <Box sx={{ width: "100%", minHeight: 300 }} my={2}>
      {title && (
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height: 300 }}>
        <BarChart
          xAxis={[
            {
              scaleType: "band",
              data: xAxisData,
              tickLabelStyle: { fontSize: 12 },
            },
          ]}
          yAxis={[
            {
              tickLabelStyle: { fontSize: 12 },
            },
          ]}
          series={series}
          height={300}
          slotProps={{
            legend: {
              position: { vertical: "top", horizontal: "end" },
            },
          }}
          grid={{ vertical: true, horizontal: true }}
          borderRadius={4}
        />
      </Box>
    </Box>
  );
};

const renderTable = (table: TableData) => {
  const { headers, rows } = table;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell key={index} sx={{ fontWeight: 600 }}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex} hover>
              {row.map((cell, cellIndex) => (
                <TableCell key={cellIndex}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const ChatMessage = ({ role, content, timestamp }: ChatMessageProps) => {
  const theme = useTheme();
  const isUser = role === "user";

  const renderContent = () => {
    if (typeof content === "string") {
      return (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            color: isUser ? "inherit" : undefined,
          }}
        >
          {content}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {content.map((item, index) => {
          if (item.type === "text" && item.content) {
            return (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  color: isUser ? "inherit" : undefined,
                }}
              >
                {item.content}
              </Typography>
            );
          }
          if (item.type === "chart" && item.chart) {
            return <Box key={index}>{renderChart(item.chart, theme)}</Box>;
          }
          if (item.type === "table" && item.table) {
            return <Box key={index}>{renderTable(item.table)}</Box>;
          }
          if (item.type === "list" && item.list) {
            return (
              <Box key={index} component="ul" sx={{ m: 0, pl: 3 }}>
                {item.list.map((li, liIndex) => (
                  <Typography
                    key={liIndex}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 0.5,
                      color: isUser ? "inherit" : undefined,
                    }}
                  >
                    {li}
                  </Typography>
                ))}
              </Box>
            );
          }
          return null;
        })}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        mb: 2,
        flexDirection: isUser ? "row-reverse" : "row",
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: isUser
            ? theme.palette.primary.main
            : theme.palette.secondary.main,
          fontSize: "0.875rem",
        }}
      >
        {isUser ? "U" : "CA"}
      </Avatar>
      <Box sx={{ flex: 1, maxWidth: "85%" }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: isUser
              ? theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.08)
              : theme.palette.action.hover,
            color: theme.palette.text.primary,
            borderRadius: 2.5,
            border: isUser
              ? `1px solid ${alpha(theme.palette.primary.main, 0.25)}`
              : `1px solid ${theme.palette.divider}`,
          }}
        >
          {renderContent()}
        </Paper>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mt: 0.5,
            textAlign: isUser ? "right" : "left",
            px: 1,
          }}
        >
          {timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>
      </Box>
    </Box>
  );
};
